import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { Link } from "react-router-dom";
import { debounce } from 'lodash';
import moment from "moment";
import {
  Card,
  Row,
  Col,
  Table,
  InputNumber,
  Space,
  Typography,
  Alert,
  Progress,
  Spin,
  Statistic,
  Button,
} from "antd";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import {
  fetchActiveOrganizations,
  fetchRemovebgCredits,
  fetchAllRemovebgOrders,
} from "../../graphql/controllers/fetchAICredits";
import { getEligiblePredictionCredits } from "./predictionEligibility";

const { Title: AntTitle, Text } = Typography;

const CreditiAI = ({ embedded = false }) => {
  const [loading, setLoading] = useState(false);
  const [creditData, setCreditData] = useState([]);
  const [minBalanceThreshold, setMinBalanceThreshold] = useState(0);
  const [maxBalanceThreshold, setMaxBalanceThreshold] = useState(null);
  const [totalCredits, setTotalCredits] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Predictions state
  const [predictionsLoading, setPredictionsLoading] = useState(false);
  const [predictionLookbackDays, setPredictionLookbackDays] = useState(90);
  const [predLowBalanceThreshold, setPredLowBalanceThreshold] = useState(200);
  const [predCoverageDaysThreshold, setPredCoverageDaysThreshold] = useState(5);
  const [predMinScore, setPredMinScore] = useState(50);
  const [predictions, setPredictions] = useState([]);
  const allOrdersRef = useRef([]);
  const activeProductionOrgsRef = useRef([]);
  const [predictionsLastUpdated, setPredictionsLastUpdated] = useState(null);

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();

  const fetchCreditData = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getTokenSilently();

      // Fetch credit balances, sorted by highest balance first
      const { credits, count } = await fetchRemovebgCredits(
        token,
        isProd,
        10000,
        0
      );

      // Filter credits based on thresholds
      const minThreshold = Number(minBalanceThreshold) || 0;
      const maxThreshold =
        maxBalanceThreshold === null || maxBalanceThreshold === undefined || isNaN(Number(maxBalanceThreshold))
          ? null
          : Number(maxBalanceThreshold);

      const filteredCredits = credits.filter((credit) => {
        const withinMin = credit.totalCredits >= minThreshold;
        const withinMax = maxThreshold === null ? true : credit.totalCredits <= maxThreshold;
        return withinMin && withinMax;
      });

      // Calculate total credits across all organizations
      const total = credits.reduce(
        (sum, credit) => sum + credit.totalCredits,
        0
      );
      setTotalCredits(total);

      setCreditData(filteredCredits);
      setPagination((prev) => ({
        ...prev,
        total: count,
      }));
    } catch (error) {
      console.error("Error fetching credit data:", error);
    } finally {
      setLoading(false);
    }
  }, [getTokenSilently, isProd, minBalanceThreshold, maxBalanceThreshold]);

  // Initialize with credit data on component mount
  useEffect(() => {
    fetchCreditData();
  }, [fetchCreditData]);

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  // Create a debounced version of the filter function (300ms delay)
  const debouncedFetchCreditData = useCallback(
    (minVal, maxVal) => {
      // Create a debounced function that executes after 300ms delay
      const debouncedFn = debounce(async (minParam, maxParam) => {
        try {
          setLoading(true);
          const token = await getTokenSilently();

          // Fetch credit balances, sorted by highest balance first
          const { credits } = await fetchRemovebgCredits(
            token,
            isProd,
            10000,
            0
          );

          // Use the passed values rather than state
          const minThreshold = Number(minParam) || 0;
          const maxThreshold =
            maxParam === null || maxParam === undefined || isNaN(Number(maxParam))
              ? null
              : Number(maxParam);

          // Filter credits based on both thresholds
          const filteredCredits = credits.filter((credit) => {
            const withinMin = credit.totalCredits >= minThreshold;
            const withinMax = maxThreshold === null ? true : credit.totalCredits <= maxThreshold;
            return withinMin && withinMax;
          });

          // Calculate total credits across all organizations
          const total = credits.reduce(
            (sum, credit) => sum + credit.totalCredits,
            0
          );

          // Update state with the new values
          setTotalCredits(total);
          setCreditData(filteredCredits);

          // Reset pagination to first page when filter changes
          setPagination({
            current: 1,
            pageSize: 10,
            total: filteredCredits.length, // Use the actual filtered count, not the total count
          });
        } catch (error) {
          console.error("Error filtering data:", error);
        } finally {
          setLoading(false);
        }
      }, 300);

      // Execute the debounced function
      debouncedFn(minVal, maxVal);
    },
    [getTokenSilently, isProd]
  );

  const handleMinThresholdChange = (value) => {
    const sanitized = typeof value === 'number' && !isNaN(value) && value >= 0 ? value : 0;
    // Ensure min <= max (if max is set)
    setMinBalanceThreshold(sanitized);
    if (maxBalanceThreshold !== null && maxBalanceThreshold !== undefined && !isNaN(Number(maxBalanceThreshold))) {
      if (Number(maxBalanceThreshold) < sanitized) {
        setMaxBalanceThreshold(sanitized);
        debouncedFetchCreditData(sanitized, sanitized);
        return;
      }
    }
    debouncedFetchCreditData(sanitized, maxBalanceThreshold);
  };

  const handleMaxThresholdChange = (value) => {
    // Allow null to mean "no upper limit"
    if (value === null || value === undefined || value === '') {
      setMaxBalanceThreshold(null);
      debouncedFetchCreditData(minBalanceThreshold, null);
      return;
    }
    const num = Number(value);
    if (isNaN(num)) return;
    const sanitized = num < 0 ? 0 : num;
    // Ensure max >= min
    if (sanitized < Number(minBalanceThreshold)) {
      setMinBalanceThreshold(sanitized);
      setMaxBalanceThreshold(sanitized);
      debouncedFetchCreditData(sanitized, sanitized);
      return;
    }
    setMaxBalanceThreshold(sanitized);
    debouncedFetchCreditData(minBalanceThreshold, sanitized);
  };



  const columns = [
    {
      title: "Org",
      dataIndex: "orgName",
      key: "orgName",
      sorter: (a, b) => (a.orgName || "").localeCompare(b.orgName || ""),
      render: (orgName, record) => (
        <Link to={`/orgs/${record.orgId}`}>
          <span style={{ fontWeight: "medium" }}>
            {orgName || <span style={{ color: "#999" }}>Unknown</span>}
          </span>
        </Link>
      ),
    },
    {
      title: "Crediti Gratuiti",
      dataIndex: "freeCredits",
      key: "freeCredits",
      sorter: (a, b) => a.freeCredits - b.freeCredits,
      render: (freeCredits) => (
        <span>{freeCredits || 0}</span>
      ),
    },
    {
      title: "Crediti Pagati",
      dataIndex: "paidCredits",
      key: "paidCredits",
      sorter: (a, b) => a.paidCredits - b.paidCredits,
      render: (paidCredits) => (
        <span>{paidCredits || 0}</span>
      ),
    },
    {
      title: "Bilancio Totale",
      dataIndex: "totalCredits",
      key: "totalCredits",
      defaultSortOrder: "descend",
      sorter: (a, b) => a.totalCredits - b.totalCredits,
      render: (totalCredits, record) => {
        // Calculate the total from the individual values to ensure consistency
        const total = (record.freeCredits || 0) + (record.paidCredits || 0);
        return <span style={{ fontWeight: "bold" }}>{total}</span>;
      },
    },
    {
      title: "% of Total",
      key: "percentage",
      render: (_, record) => {
        const percentage =
          totalCredits > 0 ? (record.totalCredits / totalCredits) * 100 : 0;
        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Progress
              percent={percentage.toFixed(1)}
              size="small"
              format={(percent) => `${percent}%`}
              strokeColor={percentage > 20 ? "#3D5864" : "#5DADE2"}
            />
          </Space>
        );
      },
    },
  ];

  // ---------- Predictions logic ----------
  const median = (arr) => {
    const a = arr.slice().sort((x, y) => x - y);
    const n = a.length;
    if (n === 0) return 0;
    const mid = Math.floor(n / 2);
    return n % 2 === 0 ? (a[mid - 1] + a[mid]) / 2 : a[mid];
  };

  const average = (arr) => (arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0);

  const modeDayOfMonth = (dates) => {
    if (!dates || dates.length === 0) return null;
    const counts = {};
    dates.forEach((d) => {
      const day = moment(d).date();
      counts[day] = (counts[day] || 0) + 1;
    });
    let best = null;
    let max = -1;
    Object.entries(counts).forEach(([day, cnt]) => {
      if (cnt > max) {
        max = cnt;
        best = Number(day);
      }
    });
    return best;
  };

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const computePredictions = useCallback(() => {
    if (!creditData || creditData.length === 0) {
      setPredictions([]);
      return;
    }

    const lookbackStart = moment().subtract(predictionLookbackDays, "days").startOf("day");

    // Build org balance map
    const balances = new Map();
    const eligibleCredits = getEligiblePredictionCredits(
      creditData,
      activeProductionOrgsRef.current
    );
    eligibleCredits.forEach((c) => {
      if (!c.orgId) return;
      balances.set(c.orgId, {
        orgId: c.orgId,
        orgName: c.orgName,
        balance: (c.freeCredits || 0) + (c.paidCredits || 0),
      });
    });

    // Filter orders for lookback and relevant orgs
    const orders = (allOrdersRef.current || []).filter((o) => {
      if (!o.orgId) return false;
      if (!balances.has(o.orgId)) return false; // focus on orgs with wallet
      const t = moment(o.createdAt);
      return t.isSameOrAfter(lookbackStart);
    });

    // Organize per org
    const orgMap = new Map();
    orders.forEach((o) => {
      if (!orgMap.has(o.orgId)) {
        orgMap.set(o.orgId, {
          orgId: o.orgId,
          orgName: o.orgName || (balances.get(o.orgId)?.orgName ?? "Unknown"),
          usageByDay: {},
          subscriptionTopups: [],
          addonTopups: [],
          lastAddon: null,
        });
      }
      const entry = orgMap.get(o.orgId);

      // Usage: sum negative values absolute per day
      const freeUsage = o.freeCredits && o.freeCredits < 0 ? Math.abs(o.freeCredits) : 0;
      const paidUsage = o.paidCredits && o.paidCredits < 0 ? Math.abs(o.paidCredits) : 0;
      const used = freeUsage + paidUsage;
      if (used > 0) {
        const day = moment(o.createdAt).format("YYYY-MM-DD");
        entry.usageByDay[day] = (entry.usageByDay[day] || 0) + used;
      }

      // Topups: positive values
      const posFree = o.freeCredits && o.freeCredits > 0 ? o.freeCredits : 0;
      const posPaid = o.paidCredits && o.paidCredits > 0 ? o.paidCredits : 0;
      const topup = posFree + posPaid;
      if (o.isPaid === true && topup > 0) {
        const isAddon = o.originalNote === "Acquisto Crediti IA";
        const topupObj = { amount: topup, date: o.createdAt };
        if (isAddon) {
          entry.addonTopups.push(topupObj);
          if (!entry.lastAddon || moment(o.createdAt).isAfter(entry.lastAddon.date)) {
            entry.lastAddon = topupObj;
          }
        } else {
          entry.subscriptionTopups.push(topupObj);
        }
      }
    });

    const results = [];
    const EPS = 1e-6;

    // Helper to get averages over day windows
    const getWindowStats = (usageByDay, days, offsetDays = 0) => {
      const values = [];
      for (let i = days - 1; i >= 0; i--) {
        const d = moment().subtract(i + offsetDays, "days").format("YYYY-MM-DD");
        values.push(usageByDay[d] || 0);
      }
      const avg = average(values);
      const med = median(values);
      return { avg, med };
    };

    const balancesArr = Array.from(balances.values());

    balancesArr.forEach((b) => {
      const data = orgMap.get(b.orgId) || {
        usageByDay: {},
        subscriptionTopups: [],
        addonTopups: [],
        lastAddon: null,
        orgId: b.orgId,
        orgName: b.orgName,
      };

      // Usage stats
      const last7 = getWindowStats(data.usageByDay, 7);
      const prev7 = getWindowStats(data.usageByDay, 7, 7);
      const last30 = getWindowStats(data.usageByDay, 30);
      const burn7 = last7.med > 0 ? last7.med : last7.avg; // robust to spikes
      const burn30 = last30.med > 0 ? last30.med : last30.avg;
      const burnAdj = Math.max(burn7, EPS);

      // Trend factor based on 7d vs previous 7d
      const trendRaw = prev7.avg > 0 ? (last7.avg - prev7.avg) / prev7.avg : 0;
      const trendFactor = clamp(trendRaw, 0, 1);

      // Subscription estimate
      const subAmounts = data.subscriptionTopups.map((t) => t.amount);
      const subDates = data.subscriptionTopups.map((t) => t.date);
      const subAmountMedian = median(subAmounts);
      const subDay = modeDayOfMonth(subDates);
      let nextSubDate = null;
      if (subDay) {
        const now = moment();
        const thisMonth = now.clone().date(subDay);
        nextSubDate = thisMonth.isAfter(now) ? thisMonth : now.clone().add(1, "month").date(subDay);
      }

      // Days coverage
      const daysCoverage = b.balance / burnAdj;

      // Predict depletion/need
      const targetBalance = Math.max(predLowBalanceThreshold, predCoverageDaysThreshold * burnAdj);
      // approximate time to reach target with trend adjustment
      const growth = 1 + 0.5 * trendFactor; // mild sensitivity
      const effBurn = Math.max(burnAdj * growth, EPS);

      // Balance until next sub
      let predictedDate = null;
      let reason = [];
      let balanceAtPrediction = b.balance;

      const todayStart = moment().startOf("day");
      if (nextSubDate && nextSubDate.isAfter(todayStart)) {
        const daysToSub = Math.max(0, nextSubDate.diff(todayStart, "days"));
        const balBeforeSub = b.balance - daysToSub * effBurn;
        if (balBeforeSub < targetBalance) {
          predictedDate = todayStart.clone().add(Math.ceil((b.balance - targetBalance) / effBurn), "days");
          balanceAtPrediction = Math.max(targetBalance, balBeforeSub);
          reason.push("copertura insufficiente prima del rinnovo abbonamento");
        } else {
          // After sub
          const balAfterSub = balBeforeSub + (subAmountMedian || 0);
          const extraDays = Math.ceil((balAfterSub - targetBalance) / effBurn);
          predictedDate = nextSubDate.clone().add(extraDays, "days");
          balanceAtPrediction = balAfterSub - extraDays * effBurn;
          reason.push("copertura dopo abbonamento");
        }
      } else {
        // No known subscription pattern
        const daysToTarget = Math.ceil((b.balance - targetBalance) / effBurn);
        predictedDate = todayStart.clone().add(Math.max(0, daysToTarget), "days");
        balanceAtPrediction = b.balance - Math.max(0, daysToTarget) * effBurn;
        reason.push("nessun rinnovo abbonamento previsto");
      }

      // History factors
      const hasAddOnHistory = data.addonTopups.length > 0;
      const lastAddon = data.lastAddon;
      const lastAddonDays = lastAddon ? Math.max(0, moment().diff(moment(lastAddon.date), "days")) : null;
      const addonMedian = median(data.addonTopups.map((t) => t.amount));

      // Score
      const urgency = clamp(1 - daysCoverage / Math.max(predCoverageDaysThreshold, 1), 0, 1);
      const historyFactor = hasAddOnHistory ? 1 : 0.3;
      const recency = lastAddonDays != null ? Math.exp(-lastAddonDays / 90) : 0;
      const score = Math.round(100 * (0.5 * urgency + 0.2 * historyFactor + 0.2 * trendFactor + 0.1 * recency));

      // Predicted amount
      const daysToCover = 14; // aim for 2 weeks buffer
      const amountByDays = Math.max(0, daysToCover * effBurn);
      const amountByHistory = addonMedian || 0;
      const amountToTarget = Math.max(0, targetBalance - balanceAtPrediction);
      const predictedAmount = Math.round(Math.max(amountByDays, amountByHistory, amountToTarget));

      const res = {
        orgId: b.orgId,
        orgName: b.orgName,
        balance: Math.round(b.balance),
        usage7d: Math.round(burn7),
        usage30d: Math.round(burn30),
        daysCoverage: Number.isFinite(daysCoverage) ? Number(daysCoverage.toFixed(1)) : null,
        nextSubscription: nextSubDate ? nextSubDate.format("YYYY-MM-DD") : null,
        nextSubscriptionAmount: Math.round(subAmountMedian || 0),
        lastAddOn: lastAddon ? moment(lastAddon.date).format("YYYY-MM-DD") : null,
        lastAddOnAmount: lastAddon ? lastAddon.amount : null,
        predictedBuyDate: predictedDate ? predictedDate.format("YYYY-MM-DD") : null,
        predictedAmount,
        score,
        reasons: reason,
      };

      results.push(res);
    });

    // Filter and sort
    const filtered = results
      .filter((r) => r.usage30d > 0) // must have some usage
      .filter((r) => r.score >= predMinScore)
      .sort((a, b) => {
        if (a.predictedBuyDate && b.predictedBuyDate) {
          if (a.predictedBuyDate === b.predictedBuyDate) return b.score - a.score;
          return a.predictedBuyDate.localeCompare(b.predictedBuyDate);
        }
        if (a.predictedBuyDate && !b.predictedBuyDate) return -1;
        if (!a.predictedBuyDate && b.predictedBuyDate) return 1;
        return b.score - a.score;
      });

    setPredictions(filtered);
    setPredictionsLastUpdated(moment());
  }, [creditData, predictionLookbackDays, predLowBalanceThreshold, predCoverageDaysThreshold, predMinScore]);

  const fetchPredictionOrders = useCallback(async () => {
    try {
      setPredictionsLoading(true);
      const token = await getTokenSilently();
      const end = moment().endOf("day");
      const start = moment().subtract(predictionLookbackDays, "days").startOf("day");
      const [{ orders }, activeProductionOrgs] = await Promise.all([
        fetchAllRemovebgOrders(
          token,
          isProd,
          start.toISOString(),
          end.toISOString()
        ),
        fetchActiveOrganizations(token, isProd),
      ]);
      allOrdersRef.current = orders || [];
      activeProductionOrgsRef.current = activeProductionOrgs || [];
      computePredictions();
    } catch (e) {
      console.error("Error fetching prediction orders:", e);
      allOrdersRef.current = [];
      activeProductionOrgsRef.current = [];
      computePredictions();
    } finally {
      setPredictionsLoading(false);
    }
  }, [getTokenSilently, isProd, predictionLookbackDays, computePredictions]);

  // Trigger predictions load when balances ready or controls change
  useEffect(() => {
    if (creditData && creditData.length > 0) {
      fetchPredictionOrders();
    }
  }, [creditData, fetchPredictionOrders]);

  // Recompute predictions when thresholds change without refetching orders
  useEffect(() => {
    if (allOrdersRef.current.length > 0) {
      computePredictions();
    }
  }, [computePredictions]);

  const predictionColumns = [
    {
      title: "Org",
      dataIndex: "orgName",
      key: "orgName",
      render: (text, record) => (
        <Link to={`/orgs/${record.orgId}/ai`}>{text}</Link>
      ),
      sorter: (a, b) => (a.orgName || "").localeCompare(b.orgName || ""),
    },
    {
      title: "Saldo",
      dataIndex: "balance",
      key: "balance",
      sorter: (a, b) => a.balance - b.balance,
      render: (v) => v?.toLocaleString(),
    },
    {
      title: "Uso 7g",
      dataIndex: "usage7d",
      key: "usage7d",
      sorter: (a, b) => a.usage7d - b.usage7d,
    },
    {
      title: "Uso 30g",
      dataIndex: "usage30d",
      key: "usage30d",
      sorter: (a, b) => a.usage30d - b.usage30d,
    },
    {
      title: "Copertura (gg)",
      dataIndex: "daysCoverage",
      key: "daysCoverage",
      sorter: (a, b) => (a.daysCoverage || 0) - (b.daysCoverage || 0),
    },
    {
      title: "Pross. Abbonamento",
      key: "nextSubscription",
      render: (_, r) => (r.nextSubscription ? `${r.nextSubscription} (${r.nextSubscriptionAmount})` : "–"),
      sorter: (a, b) => (a.nextSubscription || "").localeCompare(b.nextSubscription || ""),
    },
    {
      title: "Ultimo Add-on",
      key: "lastAddOn",
      render: (_, r) => (r.lastAddOn ? `${r.lastAddOn} (${r.lastAddOnAmount})` : "–"),
      sorter: (a, b) => (a.lastAddOn || "").localeCompare(b.lastAddOn || ""),
    },
    {
      title: "Data Prevista",
      dataIndex: "predictedBuyDate",
      key: "predictedBuyDate",
      sorter: (a, b) => (a.predictedBuyDate || "").localeCompare(b.predictedBuyDate || ""),
    },
    {
      title: "Importo Previsto",
      dataIndex: "predictedAmount",
      key: "predictedAmount",
      sorter: (a, b) => (a.predictedAmount || 0) - (b.predictedAmount || 0),
      render: (v) => (v != null ? v.toLocaleString() : "–"),
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
      sorter: (a, b) => a.score - b.score,
      render: (v) => (
        <Space direction="vertical" style={{ width: 80 }}>
          <Progress percent={v} size="small" showInfo={false} strokeColor={v >= 70 ? "#3f8600" : v >= 50 ? "#faad14" : "#cf1322"} />
          <span style={{ fontWeight: 600 }}>{v}</span>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: embedded ? 0 : "20px" }}>
      {!embedded && <AntTitle level={2}>Dashboard Crediti AI</AntTitle>}

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={24} md={12} lg={12}>
          <Card>
            <Statistic
              title="Crediti erogati nei wallet"
              value={totalCredits}
              loading={loading}
              precision={0}
              suffix="credits"
              formatter={value => {
                if (value >= 1000) {
                  return `${(value / 1000).toFixed(1)}K`;
                }
                return value;
              }}
            />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Includendo tutte le organizzazioni</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={24} md={12} lg={12}>
          <Card>
            <Statistic
              title="Orgs con crediti nel wallet"
              value={creditData.length}
              loading={loading}
              suffix="orgs"
            />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                {maxBalanceThreshold === null || maxBalanceThreshold === undefined
                  ? `Filtro: ≥ ${minBalanceThreshold} crediti`
                  : `Filtro: ${minBalanceThreshold} – ${maxBalanceThreshold} crediti`}
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Predictions Card */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card
            title="Previsioni acquisti Add-on (beta)"
            extra={
              <Space wrap>
                <Text>Periodo:</Text>
                <Button type={predictionLookbackDays === 30 ? 'primary' : 'default'} onClick={() => setPredictionLookbackDays(30)}>
                  30g
                </Button>
                <Button type={predictionLookbackDays === 90 ? 'primary' : 'default'} onClick={() => setPredictionLookbackDays(90)}>
                  90g
                </Button>
                <Button type={predictionLookbackDays === 180 ? 'primary' : 'default'} onClick={() => setPredictionLookbackDays(180)}>
                  180g
                </Button>
                <Text>Min saldo:</Text>
                <InputNumber min={0} value={predLowBalanceThreshold} onChange={(v) => setPredLowBalanceThreshold(Number(v) || 0)} />
                <Text>Copertura min (gg):</Text>
                <InputNumber min={0} value={predCoverageDaysThreshold} onChange={(v) => setPredCoverageDaysThreshold(Number(v) || 0)} />
                <Text>Score min:</Text>
                <InputNumber min={0} max={100} value={predMinScore} onChange={(v) => setPredMinScore(Number(v) || 0)} />
              </Space>
            }
          >
            <div style={{ marginBottom: 12 }}>
              <Alert
                message={`Solo org attive con istanza production (free e demo escluse). Calcolate su ${predictionLookbackDays} giorni; soglie: saldo ≥ ${predLowBalanceThreshold}, copertura ≥ ${predCoverageDaysThreshold}gg, score ≥ ${predMinScore}`}
                type="info"
                showIcon
              />
              {predictionsLastUpdated && (
                <Text type="secondary">Ultimo aggiornamento: {predictionsLastUpdated.format('DD/MM/YYYY HH:mm')}</Text>
              )}
            </div>
            <Spin spinning={predictionsLoading}>
              <Table
                dataSource={predictions}
                columns={predictionColumns}
                rowKey={(r) => r.orgId}
                pagination={{ pageSize: 10 }}
              />
            </Spin>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card
            title="Wallet Crediti"
            extra={
              <Space>
                <Link to="/giotto/ai?view=statistics">
                  <Button type="primary">Vai ai Grafici</Button>
                </Link>
                <Text>Intervallo crediti:</Text>
                <InputNumber
                  min={0}
                  value={minBalanceThreshold}
                  onChange={handleMinThresholdChange}
                  placeholder="Min"
                />
                <Text>–</Text>
                <InputNumber
                  min={0}
                  value={maxBalanceThreshold}
                  onChange={handleMaxThresholdChange}
                  placeholder="Max (vuoto = nessun limite)"
                />
              </Space>
            }
          >
            <Alert
              message={
                maxBalanceThreshold === null || maxBalanceThreshold === undefined
                  ? "Mostra solo le organizzazioni con crediti sopra o uguali al minimo"
                  : "Mostra solo le organizzazioni con crediti entro l'intervallo"
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Spin spinning={loading}>
              <Table
                dataSource={creditData}
                columns={columns}
                rowKey="id"
                pagination={pagination}
                onChange={handleTableChange}
              />
            </Spin>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CreditiAI;
